import { useMemo, useState } from "react";
import { Modal, Form, Input, Rate, message } from "antd";
import SearchBar from "../../../components/searchBar";
import AddButton from "../../../components/addButton";
import CommonTable from "../../../components/commonTable";
import CustomPagination from "../../../components/pagination";
import { formatDateTime } from "../../../utils/date";
import { useGetCallingFeedbacksQuery, useAddCallingFeedbackMutation } from "../../../services/callingFeedback";
import getColumns, { ratingColor, CallingFeedbackRow } from "./columns";
import "./styles.scss";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface RawCallingFeedback {
  _id?:         string;
  description?: string;
  rating?:      number;
  createdAt?:   string;
  dateTime?:    string;
  createdBy?:   { name?: string } | string;
}

interface CreateFeedbackFormValues {
  description: string;
  rating:      number;
}

/* ─── Helper ─────────────────────────────────────────────────────────── */

const mapToRow = (fb: RawCallingFeedback, idx: number): CallingFeedbackRow => ({
  key:       fb._id || String(idx),
  dateTime:  formatDateTime(fb.dateTime || fb.createdAt),
  feedback:  fb.description || "-",
  rating:    fb.rating ?? 0,
  createdBy: (typeof fb.createdBy === "object" ? fb.createdBy?.name : fb.createdBy) || "-",
});

/* ─── Component ──────────────────────────────────────────────────────── */

// This is a global feed (GET /feedback-ratings has no per-user scoping),
// so it's not actually filtered to the user whose detail page it's shown on.
const CallingFeedback = () => {
  const [searchText, setSearchText] = useState("");
  const [page,       setPage]       = useState(1);
  const [pageSize,   setPageSize]   = useState(10);
  const [modalOpen,  setModalOpen]  = useState(false);
  const [form]                      = Form.useForm<CreateFeedbackFormValues>();

  const { data, isLoading } = useGetCallingFeedbacksQuery({ page, limit: pageSize });
  const [addCallingFeedback, { isLoading: submitting }] = useAddCallingFeedbackMutation();

  const rows = useMemo<CallingFeedbackRow[]>(() => {
    const list = (data as any)?.data;
    const mapped = Array.isArray(list) ? list.map(mapToRow) : [];
    // Server-side `search` isn't confirmed supported by this API — filter the
    // current page client-side as a fallback so the search bar still works.
    if (!searchText) return mapped;
    const q = searchText.toLowerCase();
    return mapped.filter(r => r.feedback.toLowerCase().includes(q) || r.createdBy.toLowerCase().includes(q));
  }, [data, searchText]);

  const ratingWatch = Form.useWatch("rating", form) || 0;

  const handleCreate = async (values: CreateFeedbackFormValues) => {
    try {
      await addCallingFeedback({ description: values.description, rating: values.rating, name: values.description }).unwrap();
      message.success("Feedback added successfully");
      form.resetFields();
      setModalOpen(false);
    } catch {
      /* error toast handled globally by badRequestHandler */
    }
  };

  return (
    <div className="calling-feedback-section">
      <div className="cf-header-row">
        <SearchBar
          value={searchText}
          onChange={(v: string) => setSearchText(v)}
          placeholder="Search feedback..."
        />
        <AddButton onClick={() => setModalOpen(true)}>Create Feedback</AddButton>
      </div>

      <CommonTable
        columns={getColumns()}
        dataSource={rows}
        loading={isLoading}
        rowKey="key"
        scroll={{ x: 900 }}
      />

      <CustomPagination
        current={(data as any)?.pagination?.page || page}
        pageSize={(data as any)?.pagination?.limit || pageSize}
        total={(data as any)?.pagination?.total || rows.length}
        onPageChange={(p: number) => setPage(p)}
        onPageSizeChange={(size: number) => { setPageSize(size); setPage(1); }}
      />

      <Modal
        open={modalOpen}
        title="Create Feedback"
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        onOk={() => form.submit()}
        okText="Save"
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleCreate} className="calling-feedback-form">
          <Form.Item name="description" label="Feedback" rules={[{ required: true, message: "Please type feedback" }]}>
            <Input.TextArea placeholder="Type feedback..." rows={4} />
          </Form.Item>
          <Form.Item name="rating" label="Rating" rules={[{ required: true, message: "Please select a rating" }]}>
            <Rate count={5} style={{ color: ratingWatch ? ratingColor(ratingWatch) : undefined, fontSize: 28 }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CallingFeedback;
